import React, { useState, useEffect } from "react";
import {
  Combobox,
  Option,
  Input,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Button as FluentButton,
  Spinner,
  Menu, MenuItem, MenuTrigger, MenuPopover, MenuList,
  makeStyles,
} from "@fluentui/react-components";
import { ChevronDown12Regular, Checkmark12Regular } from "@fluentui/react-icons";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import Button from "./Button";
import ConfirmationForm from "./ConfirmationForm";
import { prepareSaveContent } from "../save";
import { getColumnLetter, name, pascalToSentenceCase, id, utc } from "../utils";
import api from "../../api/api";

interface ReportParamsInterface {
  authToken: string;
  identityProvider: string;
  shouldRefresh?: Date;
}

const useStyles = makeStyles({
  spinnerContainer: {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 1000,
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    width: "100vw",
    height: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  parameter: {
    paddingBottom: "8px",
  },
  parameterLabel: {
    display: "inline-block",
    minWidth: "100px",
    color: "#001C71",
  },
  parameterWarning: {
    color: "red",
  },
  parameterControl: {
    fontSize: "inherit",
    width: "280px",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    border: "0.25pt solid #C4E1E0",
    borderRadius: "6px",
    "&:hover": {
      border: "0.25pt solid #7FBDBA",
    },
    "&:disabled": {
      border: "0.25pt solid #E0E0E0",
      backgroundColor: "#F5F5F5",
      color: "#A0A0A0",
      opacity: 0.6,
      cursor: "not-allowed",
    },
  },
  commandArea: {
    paddingTop: "8px",
  },
  saveCommandArea: {
    marginTop: "16px",
    marginLeft: "-20px",
    marginRight: "-20px",
    borderTop: "0.25pt solid #E0E0E0",
    paddingTop: "16px",
    paddingLeft: "20px",
    paddingRight: "20px",
    display: "flex",
    gap: "8px",
  },
  button: {
    minWidth: "265px",
    paddingTop: "3px",
    paddingBottom: "3px",
    paddingLeft: "20px",
    paddingRight: "20px",
    border: "0.25pt solid #7FBDBA",
    backgroundColor: "#001C71",
    color: "#ffffff",
    fontWeight: "bold",
    "&:hover": {
      border: "0.25pt solid #001C71",
      backgroundColor: "#001C71",
      color: "#ffffff",
    },
    "&:disabled": {
      border: "0.25pt solid #E0E0E0",
      backgroundColor: "#F5F5F5",
      color: "#A0A0A0",
      opacity: 0.6,
      cursor: "not-allowed",
    },
  },
  wideButton: {
    minWidth: "280px",
    paddingTop: "3px",
    paddingBottom: "3px",
    paddingLeft: "20px",
    paddingRight: "20px",
    border: "0.25pt solid #7FBDBA",
    backgroundColor: "#001C71",
    color: "#ffffff",
    fontWeight: "bold",
    "&:hover": {
      border: "0.25pt solid #001C71",
      backgroundColor: "#001C71",
      color: "#ffffff",
    },
    "&:disabled": {
      border: "0.25pt solid #E0E0E0",
      backgroundColor: "#F5F5F5",
      color: "#A0A0A0",
      opacity: 0.6,
      cursor: "not-allowed",
    },
  },
  saveButton: {
    width: "90px",
    paddingLeft: "20px",
    paddingRight: "20px",
    border: "0.25pt solid #7FBDBA",
    backgroundColor: "#001C71",
    color: "#ffffff",
    fontWeight: "bold",
    "&:hover": {
      border: "0.25pt solid #001C71",
      backgroundColor: "#001C71",
      color: "#ffffff",
    },
    "&:disabled": {
      border: "0.25pt solid #E0E0E0",
      backgroundColor: "#F5F5F5",
      color: "#A0A0A0",
      opacity: 0.6,
      cursor: "not-allowed",
    },
  },
  dropDownButton: {
    borderRadius: "6px",
    border: "0.25pt solid #C4E1E0",
    backgroundColor: "#001C71",
    color: "#ffffff",
    fontSize: "11px",
    display: "block",
    "&:hover": {
      border: "0.25pt solid #C4E1E0",
      backgroundColor: "#001C71",
      color: "#ffffff",
    },
    "&:disabled": {
      border: "0.25pt solid #E0E0E0",
      backgroundColor: "#F5F5F5",
      color: "#A0A0A0",
      opacity: 0.6,
      cursor: "not-allowed",
    },
  },
});

type ParameterDefinition = { name: string; type: string; call: string; default: string; value?: string };

const ReportParams: React.FC<ReportParamsInterface> = ({ authToken, identityProvider, shouldRefresh }) => {
  const pageSize = 1000;
  const styles = useStyles();

  type Item = { id: string; name: string };
  type ItemsState = Record<string, Item[]>;
  type StringState = Record<string, string>;
  type BooleanState = Record<string, boolean>;

  const [items, setItemsState] = useState<ItemsState>({});
  const [udfs, setUdfs] = useState<Item[]>([]);
  const [selectedItem, setSelectedItemState] = useState<StringState>({});
  const [selectedUdfs, setSelectedUdfs] = useState<string>("");
  const [areItemsLoading, setAreItemsLoadingState] = useState<BooleanState>({});
  const [areUdfsLoading, setAreUdfsLoading] = useState<boolean>(false);
  const [canGetData, setCanGetData] = useState<boolean>(false);
  const [canGetDatas, setCanGetDatas] = useState<boolean[]>();
  const [gotData, setGotData] = useState<boolean>(true);
  const [reportName, setReportName] = useState<string>("");
  const [overWrite, setOverWrite] = useState<string>("false");
  const [parameterDefinitions, setParameterDefinitions] = useState<ParameterDefinition[]>([]);
  const [dataDefinition, setDataDefinition] = useState<ParameterDefinition>({
    name: "",
    type: "",
    call: "",
    default: "",
  });
  const [dataDefinitions, setDataDefinitions] = useState<ParameterDefinition[]>([{ name: "", type: "", call: "", default: "" }]);
  const [selectedDataDefinitionIndex, setSelectedDataDefinitionIndexState] = useState<number>(0);
  const [comboboxQuery, setComboboxQuery] = useState<string>("");
  const [udfComboboxQuery, setUdfComboboxQuery] = useState<string>("");
  const [comboboxOpen, setComboboxOpen] = useState<string>("");
  const [isComboboxOpen, setIsComboboxOpen] = useState<boolean>(false);
  const [isUdfComboboxOpen, setIsUdfComboboxOpen] = useState<boolean>(false);
  const [isGetDataConfirmationOpen, setIsGetDataConfirmationOpen] = useState<boolean>(false);
  const [isSaveConfirmationOpen, setIsSaveConfirmationOpen] = useState<boolean>(false);
  const [saveCompleted, setSaveCompleted] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [percent, setPercent] = useState<number>(0);
  const [isRefreshQueriesMessageShown, setIsRefreshQueriesMessageShown] = useState<boolean>(false);

  const setItems = (key: string, items: Item[]) => {
    setItemsState((prev) => ({ ...prev, [key]: items }));
  };
  const setAreItemsLoading = (key: string, value: boolean) => {
    setAreItemsLoadingState((prev) => ({ ...prev, [key]: value }));
  };

  const loadUdfs = async (call: string) => {
    if (!call || !call.includes("&udf=*")) {
      setUdfs([]);
      return;
    }

    let i = call.indexOf("&");
    if (i >= 0) {
      call = call.substring(0, i);
    }

    try {
      setAreUdfsLoading(true);
      const response = await api.fetch(
        `udfs/${call}`,
        authToken,
        identityProvider
      );
      if (!response.ok) throw new Error("Response not OK");
      const data: any = await response.json();
      let rowItems = data.result.map((row: any) => ({ id: row.key, name: row.name }));
      rowItems = Array.from(new Map(rowItems.map((obj) => [obj.id, obj])).values());
      setUdfs(rowItems);
    } catch (error) {
      setError("Error fetching UDFs: " + error);
    } finally {
      setAreUdfsLoading(false);
    }
  }

  const setSelectedItem = (key: string, item: string) => {
    let newSelectedItem = { ...selectedItem, [key]: item };
    setSelectedItemState(newSelectedItem);
    setTimeout(() => {
      Excel.run(async (context) => {
        await saveParams(context, newSelectedItem);
      });
      loadDependentData(parameterDefinitions, dataDefinitions, newSelectedItem, null, false, key);
    });
  };

  const setSelectedDataDefinitionIndex = (value: number, dataDefinitions: ParameterDefinition[]) => {
    setSelectedUdfs("");
    setSelectedDataDefinitionIndexState(value);
    setTimeout(async () => {
      await loadUdfs(dataDefinitions[value].call);
    });
  };

  const allResolved = (call: string, selectedItem: StringState, parameterDefinitions: ParameterDefinition[]) => {
    let i = 0;
    while (i < call.length) {
      let j = call.indexOf("%(", i);
      if (j < 0) {
        break;
      }
      let k = call.indexOf(")", j);
      if (k < 0) {
        return false;
      }
      let name = call.substring(j + 2, k);
      let nameId = id(name);
      let type = parameterDefinitions.find((p) => p.name == name)?.type;
      if (type != "text" && (selectedItem[nameId]?.length ?? 0) == 0) {
        return false;
      }
      i = k + 1;
    }
    return true;
  };

  const fillCall = (call: string, selectedItem: StringState, selectedUdfs: String = null) => {
    let i = 0;
    while (i < call.length) {
      let j = call.indexOf("%(", i);
      if (j < 0) {
        break;
      }
      let k = call.indexOf(")", j);
      if (k < 0) {
        break;
      }
      let name = call.substring(j + 2, k);
      let nameId = id(name);
      if (selectedItem[nameId]?.length ?? 0 > 0) {
        let m = call.lastIndexOf("[", j);
        let n = call.indexOf("]", k);
        if (m >= 0 && n >= 0) {
          call = call.substring(0, m) + call.substring(m + 1);
          n--;
          call = call.substring(0, n) + call.substring(n + 1);
          j--;
          k--;
        }
        let value = selectedItem[nameId];
        if (call[k + 1] == "*") {
          k++;
          value = "'" + value.replace(/, /g, "', '") + "'";
        }
        call = call.substring(0, j) + value + call.substring(k + 1);
        i = j + (value?.length ?? 0);
      } else {
        let m = call.lastIndexOf("[", j);
        let n = call.indexOf("]", k);
        if (m < 0 || n < 0) {
          i = k + 1;
          continue;
        }
        call = call.substring(0, m) + call.substring(n + 1);
        i = m;
      }
    }
    if (selectedUdfs != null) {
      call = call.replace("&udf=*", "&udf=" + encodeURIComponent(selectedUdfs.replace(/, /g, ",")).replace(/%2C/g, ","));
    }
    return call;
  };

  const dependency = (call: string, changedParameter: string) => {
    let usageName = `%(${changedParameter})`;
    return call.includes(usageName);
  }

  const loadDependentData = async (
    parameterDefinitions: ParameterDefinition[],
    dataDefinitions: ParameterDefinition[],
    selectedItem: StringState,
    newSelectedDataDefinitionIndex: number | null = null,
    initial = false,
    changedParameter?: string
  ) => {
    Excel.run(async (context) => {
      let allParameterDefinitions = parameterDefinitions.concat(dataDefinitions);
      for (let parameter of parameterDefinitions) {
        let nameId = id(parameter.name);
        if (changedParameter && (nameId == changedParameter || !dependency(parameter.call, changedParameter))) continue;
        let call = parameter.call;
        if (!allResolved(call, selectedItem, parameterDefinitions)) continue;
        let type = parameter.type;
        if (call && (type.startsWith("select") || type.startsWith("multiselect"))) {
          let idField = type.substring(type.indexOf("(") + 1, type.indexOf(",")).trim();
          let nameField = type.substring(type.indexOf(",") + 1, type.indexOf(")")).trim();
          try {
            setAreItemsLoading(nameId, true);

            call = fillCall(call, selectedItem);

            // Split call by &
            let callParts = call.split("&");
            let callEndpoint = callParts[0];
            callParts = callParts.slice(1);

            let body: any = {
              apiEndpoint: callEndpoint,
            };

            for (let part of callParts) {
              let eqIndex = part.indexOf("=");
              if (eqIndex > 0) {
                let key = part.substring(0, eqIndex);
                let value = part.substring(eqIndex + 1);
                body[key] = value;
              } else {
                body[part] = true;
              }
            }

            const response = await api.post(
              `dropdown/data`,
              body,
              authToken,
              identityProvider
            );
            if (!response.ok) throw new Error("Response not OK");
            const data: any = await response.json();
            let rowItems = data.rows.map((row: any) => ({ id: row[idField], name: row[nameField] }));
            rowItems = Array.from(new Map(rowItems.map((obj) => [obj.id, obj])).values());
            if (!type.startsWith("multiselect") && allUsesOptional(parameter.name, allParameterDefinitions)) {
              rowItems.splice(0, 0, { id: "", name: parameter.default ?? "Unspecified" });
            }
            setItems(nameId, rowItems);
            if (parameter.type.startsWith("multiselect")) {
              if (initial) {
                let values = parameter.value?.split(", ") ?? [];
                let sel = "";
                for (let value of values) {
                  let item = rowItems.find((item) => item.id == value);
                  if (item != null) {
                    if (sel) sel += ", ";
                    sel += parameter.value.toString();
                  }
                  selectedItem[nameId] = sel;
                }
              }
            } else {
              if (!rowItems.find((item) => item.id == selectedItem[nameId])) {
                let value = rowItems?.find((item) => item.id == parameter.value) ? parameter.value.toString() : "";
                selectedItem[nameId] = value;
              }
            }
          } catch (error) {
            setError("Error fetching " + nameId + " items: " + error);
          } finally {
            setAreItemsLoading(nameId, false);
          }
        } else if (type.startsWith("select") || type.startsWith("multiselect")) {
          let sheetName = "(" + type.substring(type.indexOf("(") + 1, type.indexOf(")")).trim() + ")";
          let sheet = context.workbook.worksheets.getItemOrNullObject(sheetName);
          await context.sync();
          let range = sheet.getUsedRange();
          range.load("values");
          await context.sync();
          let rowItems = range.values.map((row: any) => ({ id: row[0], name: row.length > 1 ? row[1] : row[0] }));
          setItems(nameId, rowItems);
          if (!type.startsWith("multiselect") && allUsesOptional(parameter.name, allParameterDefinitions)) {
            selectedItem[nameId] = rowItems?.find((item) => item.id == parameter.value)
              ? (parameter.value ?? (parameter.value.toString() || ""))
              : parameter.default || "";
          }
        } else if (type == "text" || type == "number") {
          if (initial) {
            selectedItem[nameId] = parameter.value ?? (parameter.default || "");
          }
        } else if (type == "date") {
          if (initial) {
            let defaultValue = parameter.default;
            if (defaultValue == "today") {
              defaultValue = new Date().toISOString().split("T")[0];
            }
            selectedItem[nameId] = parameter.value
              ? new Date(parameter.value).toISOString()
              : defaultValue
                ? new Date(defaultValue + "T12:00:00Z").toISOString()
                : null;
          }
        }
      }
      
      loadDependentDefData(parameterDefinitions, dataDefinitions, selectedItem, newSelectedDataDefinitionIndex);
    });
  };

  const loadDependentDefData = (
    parameterDefinitions: ParameterDefinition[],
    dataDefinitions: ParameterDefinition[],
    selectedItem: StringState,
    newSelectedDataDefinitionIndex: number | null = null) => {
      let allCanGetDatas = dataDefinitions.map((def) => allResolved(def.call, selectedItem, parameterDefinitions));
      setCanGetDatas(allCanGetDatas);
      setCanGetData(allCanGetDatas[newSelectedDataDefinitionIndex ?? selectedDataDefinitionIndex]);
  };

  const allUsesOptional = (name: string, parameterDefinitions: ParameterDefinition[]): boolean => {
    for (let parameter of parameterDefinitions) {
      let call = parameter.call;
      let usageName = `%(${name})`;
      if (!call.includes(usageName)) {
        continue;
      }
      let i = 0;
      while (i < call.length) {
        let j = call.indexOf(usageName, i);
        if (j < 0) {
          break;
        }
        let k = call.lastIndexOf("[", j);
        if (k < 0) {
          return false;
        }
        let l = call.indexOf("]", k);
        if (l < 0 || l < j) {
          return false;
        }
        i = j + usageName.length;
      }
    }
    return true;
  };

  useEffect(() => {
    var initial = true;

    const loadExcelConfiguration = async () => {
      const extractParam = (row: any, parameterValues: { name: string; value: string }[]) => ({
        name: row[0],
        type: row[1],
        call: row[2],
        default: row[3],
        value: parameterValues.find((p) => p.name == row[0])?.value,
      });

      Excel.run(async (context) => {
        {
          const sampleName = context.workbook.names.getItemOrNullObject("sample");
          await context.sync();
          if (!sampleName.isNullObject) {
            setGotData(false);
          }
        }

        {
          let sheet = context.workbook.worksheets.getItemOrNullObject("(parameters)");
          await context.sync();
          if (!sheet.isNullObject) {
            let templateName = "";
            let templateNameRange = context.workbook.names.getItemOrNullObject("template");
            await context.sync();
            if (!templateNameRange.isNullObject) {
              const range = templateNameRange.getRange();
              range.load("values");
              await context.sync();
              templateName = range.values[0][0] || "";
            }
            let reportNameRange = context.workbook.names.getItemOrNullObject("report");
            await context.sync();
            if (!reportNameRange.isNullObject) {
              const range = reportNameRange.getRange();
              range.load("values");
              await context.sync();
              let name = range.values[0][0] || "";
              if (name.length > 0 && name != templateName) {
                setReportName(name);
              }
            }
          }
        }

        let dataDef = null;
        let udfDataDef = null;

        let parameterValues: { name: string; value: string }[] = [];
        if (initial) {
          let sheet = context.workbook.worksheets.getItemOrNullObject("(parameters)");
          await context.sync();
          if (!sheet.isNullObject) {
            let range = sheet.getUsedRange();
            range.load("values");
            await context.sync();
            let pv = range.values.slice(2).map((row: any) => ({ name: row[0], value: row[1] }));
            dataDef = pv.find((p) => p.name == "Data");
            if (dataDef)
              pv.splice(pv.indexOf(dataDef!), 1);
            udfDataDef = pv.find((p) => p.name == "UDFs");
            if (udfDataDef)
              pv.splice(pv.indexOf(udfDataDef!), 1);
            parameterValues = pv;
          }
        }

        {
          let sheet = context.workbook.worksheets.getItemOrNullObject("(configuration)");
          let range = sheet.getUsedRange();
          range.load("values");
          await context.sync();
          let allParameterDefinitions = range.values.map((row) => extractParam(row, parameterValues));
          let dataDefinitions = [];
          // browse allParameterDefinitions in reverse order to find data definitions at the end - having type "(data)"
          for (let i = allParameterDefinitions.length - 1; i >= 0; i--) {
            let parameter = allParameterDefinitions[i];
            if (parameter.type == "(data)") {
              dataDefinitions.unshift(parameter);
            } else {
              break;
            }
          }
          let parameterDefinitions = allParameterDefinitions.slice(1, -dataDefinitions.length);
          setParameterDefinitions(parameterDefinitions);
          let dataDefinition =
            dataDefinitions.length > 0
              ? dataDefinitions[0]
              : { name: "", type: "", call: "", default: "" };
          setDataDefinition(dataDefinition);
          setDataDefinitions(dataDefinitions);
          if (dataDef) {
            let i = dataDefinitions.findIndex((def) => def.name == (dataDef ? dataDef.value : ""));
            setSelectedDataDefinitionIndex(i, dataDefinitions);
          }
          else {
            setSelectedDataDefinitionIndex(selectedDataDefinitionIndex, dataDefinitions);
          }
          if (udfDataDef) {
            setSelectedUdfs(udfDataDef.value || "");
          }
          await loadDependentData(parameterDefinitions, dataDefinitions, selectedItem, null, true);
        }

        initial = false;
      });
    };

    loadExcelConfiguration();
  }, [shouldRefresh]);

  const getSelectedOptions = (items: Item[], selectedItem: string) => {
    const selected = selectedItem?.split(", ") ?? [];
    let selectedItems = items?.filter((item) => selected.includes(item.id)).map((item) => item.id) ?? [];
    if (selectedItems.length > 0 && selectedItems.length == (items?.length ?? 0)) {
      selectedItems.push("all");
    }
    return selectedItems;
  };

  // Handle select all or deselect all
  const handleSelectAll = (nameId: string, data: any) => {
    if (items[nameId]?.length === data.selectedOptions?.filter((item) => item != "all").length) {
      setSelectedItem(nameId, ""); // Deselect all
    } else {
      setSelectedItem(nameId, items[nameId]?.map((item) => item.id).join(", ")); // Select all
    }
  };

  // Handle individual option selection
  const handleOptionSelect = (nameId: string, data: any) => {
    if (data.optionValue === "all") {
      handleSelectAll(nameId, data); // If "Select All" is clicked
    } else {
      setSelectedItem(nameId, data.selectedOptions?.join(", ") || ""); // Update selected options
    }
  };

  const handleSelectAllUdfs = (data: any) => {
    if (udfs?.length === data.selectedOptions?.filter((item) => item != "all").length) {
      setSelectedUdfs(""); // Deselect all
    } else {
      setSelectedUdfs(udfs?.map((item) => item.id).join(", ")); // Select all
    }
  };
  const handleUdfSelect = (data: any) => {
    if (data.optionValue === "all") {
      handleSelectAllUdfs(data); // If "Select All" is clicked
    } else {
      setSelectedUdfs(data.selectedOptions?.join(", ") || ""); // Update selected options
    }
  };

  const handleGetDataInternal = async (context: Excel.RequestContext, selectedDataDefinitionIndex: number) => {
    setPercent(0);
    setIsLoading(true);

    await saveParams(context, selectedItem, true);

    try {
      {
        const sampleName = context.workbook.names.getItemOrNullObject("sample");
        await context.sync();
        if (!sampleName.isNullObject) {
          sampleName.getRange().clear();
          sampleName.delete();
          setGotData(true);
        }
      }

      let columns: { field: string; type: string; name: string }[];
      {
        const specificSheetName = "(columns-" + id(dataDefinitions[selectedDataDefinitionIndex].name) + ")";
        let specificSheet = context.workbook.worksheets.getItemOrNullObject(specificSheetName);
        await context.sync();
        let sheet = !specificSheet.isNullObject ? specificSheet : context.workbook.worksheets.getItem("(columns)");
        let range = sheet.getUsedRange();
        await context.sync();
        range.load("values");
        await context.sync();
        columns = range.values.slice(1).map((row: any) => ({ field: row[0], type: row[1], name: row[2] }));

        if (columns.length == 0) {
          columns.push({ field: "id", type: "@", name: "ID" });
        }

        if (selectedUdfs) {
          const udfFields = selectedUdfs.split(", ");
          for (let udfField of udfFields) {
            if (!columns.find((c) => c.field == udfField)) {
              columns.push({ field: udfField, type: "@", name: udfs.find((u) => u.id == udfField)?.name || udfField } );
            }
          }
        }
      }

      const specificNameId = "data-" + id(dataDefinitions[selectedDataDefinitionIndex].name);
      const specificSheetName = "(" + specificNameId + ")";
      let specificSheet = context.workbook.worksheets.getItemOrNullObject(specificSheetName);
      await context.sync();
      let dataNameId = !specificSheet.isNullObject ? id(specificNameId) : "data";
      let sheetName = !specificSheet.isNullObject ? specificSheetName : "(data)";
      let sheet = !specificSheet.isNullObject ? specificSheet : context.workbook.worksheets.getItemOrNullObject("(data)");
      sheet.protection.unprotect();
      sheet.getUsedRange().clear();

      {
        const headerValues = columns.map((column) => pascalToSentenceCase(column.name || column.field));
        const allValues = [headerValues];
        const range = sheet.getRange(`A1:${getColumnLetter(columns.length)}1`);
        range.numberFormat = Array(allValues.length).fill(columns.map((c) => (c.type?.length > 0 ? c.type : "@")));
        range.values = allValues;

        let dataName = context.workbook.names.getItemOrNullObject(dataNameId);
        await context.sync();
        if (dataName.isNullObject) {
          dataName = context.workbook.names.getItem("data");
        }
        dataName.formula = `='${sheetName}'!$A$1:${getColumnLetter(columns.length)}$1`;
        await context.sync();
      }

      let userSheet = context.workbook.worksheets.getItemOrNullObject(dataDefinitions[selectedDataDefinitionIndex].name);
      await context.sync();
      if (!userSheet.isNullObject) {
        userSheet.activate();
      } else {
        context.workbook.worksheets.getFirst().activate();
      }
      await context.sync();

      let rowIndex = 1;
      let pageIndex = 0;
      let totalCount = 0;

      do {
        let call = fillCall(dataDefinitions[selectedDataDefinitionIndex].call, selectedItem, selectedUdfs);

        // Split call by &
        let callParts = call.split("&");
        let callEndpoint = callParts[0];
        callParts = callParts.slice(1);

        let body: any = {
          apiEndpoint: callEndpoint,
          pageIndex: pageIndex,
          pageSize: pageSize,
        };

        for (let part of callParts) {
          let eqIndex = part.indexOf("=");
          if (eqIndex > 0) {
            let key = part.substring(0, eqIndex);
            let value = part.substring(eqIndex + 1);
            body[key] = value;
          } else {
            body[part] = true;
          }
        }

        const response = await api.post(
          "report/data",
          body,
          authToken,
          identityProvider
        );
        if (!response.ok) throw "Response not OK";
        const data: any = await response.json();
        const rows = data.rows;
        totalCount = data.totalCount ?? 0;

        if (dataDefinition.default == "Test") {
          console.log(rows.length > 0 ? Object.keys(rows[0]).join("\n") : []);
          console.log(rows);
        }

        {
          const rowValues = rows.map((job) => columns.map((column) => job[column.field] ?? ""));
          const allValues = rowValues;
          let endRowIndex = rowIndex + rows.length;
          if (rows.length > 0) {
            const range = sheet.getRange(`A${rowIndex + 1}:${getColumnLetter(columns.length)}${endRowIndex}`);
            range.numberFormat = Array(allValues.length).fill(columns.map((c) => (c.type?.length > 0 ? c.type : "@")));
            range.values = allValues;
          }
          rowIndex = endRowIndex;
          console.log(rows.length + " rows loaded, total count: " + totalCount);
          if (rows.length == 0) {
            break;
          }
        }

        {
          let dataName = context.workbook.names.getItemOrNullObject(dataNameId);
          await context.sync();
          if (dataName.isNullObject) {
            dataName = context.workbook.names.getItem("data");
          }
          dataName.formula = `='${sheetName}'!$A$1:${getColumnLetter(columns.length)}${rowIndex}`;
        }

        pageIndex++;
        if (totalCount > 0) {
          setPercent(Math.min(100, ((pageIndex * pageSize) / totalCount) * 100));
        }
        await context.sync();
      } while (totalCount == 0 || pageIndex * pageSize < totalCount);

      sheet.protection.protect();

      {
        let sheetsSheet = context.workbook.worksheets.getItemOrNullObject("(sheets)");
        await context.sync();
        if (!sheetsSheet.isNullObject) {
          let range = sheetsSheet.getUsedRange();
          await context.sync();
          range.load("values");
          await context.sync();
          let values = range.values.slice(1).map((row: any) => ({ sheet: row[0], parameter: row[1], condition: row[2] }));
          for (let value of values) {
            let visibleSheet = context.workbook.worksheets.getItemOrNullObject(value.sheet);
            await context.sync();
            if (!visibleSheet.isNullObject) {
              visibleSheet.visibility = selectedItem[value.parameter] == value.condition ? "Visible" : "Hidden";
              await context.sync();
            }
          }
        }
      }

      refreshAllPivotTables();
      refreshAllQueries();

      setGotData(true);
    } catch (error) {
      setError("Error fetching data: " + error);
    } finally {
      setPercent(0);
      setIsLoading(false);
    }
  };

  async function refreshAllPivotTables() {
    await Excel.run(async (context) => {
      const sheets = context.workbook.worksheets;
      sheets.load("items");
      await context.sync();

      for (const sheet of sheets.items) {
        const pivots = sheet.pivotTables;
        pivots.load("items");
        await context.sync();

        for (const pivot of pivots.items) {
          pivot.refresh(); // ExcelApi 1.13+
        }
      }

      await context.sync();
    });
  }

  async function refreshAllQueries() {
    await Excel.run(async (context) => {
      const queries = context.workbook.queries;
      queries.load("items");
      await context.sync();

      if (queries.items.length > 0) {
        setIsRefreshQueriesMessageShown(true);
      }
    });
  }

  const handleGetDataConfirmationClick = async (selectedDataDefinitionIndex: number) => {
    Excel.run(async (context) => {
      await handleGetDataInternal(context, selectedDataDefinitionIndex);
      return context.sync();
    });
  };

  const handleGetDataLocal = async () => { 
    await handleGetDataConfirmationClick(selectedDataDefinitionIndex);
  };
  
  const handleGetData = async (selectedDataDefinitionIndex: number) => {
    Excel.run(async (context) => {
      let showWarning = false;
      {
        let sampleName = context.workbook.names.getItemOrNullObject("sample");
        await context.sync();
        if (sampleName.isNullObject) {
          showWarning = true;
        }
      }

      if (showWarning) {
        setIsGetDataConfirmationOpen(true);
        return context.sync();
      }

      await handleGetDataConfirmationClick(selectedDataDefinitionIndex);

      return context.sync();
    });
  };

  const saveParams = async (context: Excel.RequestContext, selectedItem: StringState, saveHeader = false) => {
    try {
      let sheet = context.workbook.worksheets.getItemOrNullObject("(parameters)");
      await context.sync();
      if (sheet.isNullObject) {
        sheet = context.workbook.worksheets.add("(parameters)");
        sheet.visibility = "Hidden";
        await context.sync();
      } else {
        sheet.protection.unprotect();
        let usedRange = sheet.getUsedRange();
        usedRange.clear();
      }

      let range = sheet.getRange("A1:B1");
      range.values = [["Analyzer:", reportName || ""]];
      await context.sync();
      if (reportName?.length > 0) {
        let reportNameRange = context.workbook.names.getItemOrNullObject("report");
        await context.sync();
        if (!reportNameRange.isNullObject) {
          reportNameRange.delete();
        }
        range = sheet.getRange("B1:B1");
        context.workbook.names.add("report", range);
      }

      let parameterValues = parameterDefinitions.map((parameter) => {
        let nameId = id(parameter.name);
        let value = selectedItem[nameId] || "";
        let text =
          (parameter.type.startsWith("select") || parameter.type.startsWith("multiselect")
            ? parameter.type.startsWith("multiselect")
              ? selectedItem[nameId]
                  ?.split(", ")
                  ?.map((id) => items[nameId]?.find((item) => item.id === id)?.name)
                  .join(", ")
              : items[nameId]?.find((item) => item.id === selectedItem[nameId])?.name
            : selectedItem[nameId]?.toString()) || "";
        return { name: parameter.name, value, text: text || "N/A" };
      });
      parameterValues.push({ name: "Data", value: dataDefinitions[selectedDataDefinitionIndex].name, text: dataDefinitions[selectedDataDefinitionIndex].name });
      if (selectedUdfs?.length ?? 0 > 0) {
        parameterValues.push({ name: "UDFs", value: selectedUdfs, text: selectedUdfs.split(", ").map((id) => udfs.find((item) => item.id === id)?.name || id).join(", ") });
      }

      if (parameterValues.length > 0) {
        sheet.getRange("A2:B2").values = [
          ["Parameters:", saveHeader ? parameterValues.map((p) => p.name + ": " + p.text).join(", ") : "..."],
        ];
        await context.sync();

        let parametersNameRange = context.workbook.names.getItemOrNullObject("parameters");
        await context.sync();
        if (!parametersNameRange.isNullObject) {
          parametersNameRange.delete();
        }
        range = sheet.getRange("B2:B2");
        context.workbook.names.add("parameters", range);
        await context.sync();

        range = sheet.getRange(`A3:C${2 + parameterValues.length}`);
        range.numberFormat = Array(parameterValues.length).fill(Array(3).fill("@"));
        range.values = parameterValues.map((pv) => [pv.name, pv.value, pv.text]);
        await context.sync();
      }

      if (saveHeader) {
        sheet.protection.protect();
        await context.sync();
      }
    } catch (error) {
      setError("Save params failed: " + error);
    }
  };

  const handleSaveInternal = async () => {
    Excel.run(async (context) => {
      setIsLoading(true);

      await saveParams(context, selectedItem, true);
      try {
        let excelContentToSave = await prepareSaveContent();

        const response = await api.post(
          `report/file?FileName=${encodeURIComponent(reportName)}&TemplateName=${encodeURIComponent(dataDefinition.default)}&OverWrite=${encodeURIComponent(overWrite)}`,
          {
            fileContent: excelContentToSave,
            lastModified: new Date().toISOString(),
          },
          authToken
        );
        if (!response.ok) {
          throw new Error("Response not OK");
        }

        console.log("Save successful.");

        setSaveCompleted(true);
      } catch (error) {
        setError("Save failed: " + error);
      } finally {
        setIsLoading(false);
        setOverWrite("false");
      }
    });
  };

  const handleSaveConfirmationClick = async () => {
    await handleSaveInternal();
  };

  const handleSave = async () => {
    setIsLoading(true);

    try {
      const response = await api.fetch(
        `report/file/exists?FileName=${encodeURIComponent(reportName)}.xlsx&TemplateName=${encodeURIComponent(dataDefinition.default)}`,
        authToken,
        identityProvider
      );
      if (response.status == 404) {
        // File does not exist, proceeding to save.
        setOverWrite("false");
        await handleSaveInternal();
      } else if (response.ok) {
        setOverWrite("true");
        setIsSaveConfirmationOpen(true);
      } else {
        throw new Error("Response not OK");
      }
    } catch (error) {
      setError("Error checking report existence: " + error);
      return;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <h3>
        {(reportName?.trim()?.length ?? 0) > 0
          ? name(reportName)
          : (dataDefinition.default?.length ?? 0) > 0
            ? name(dataDefinition.default)
            : "Analyzer"}
      </h3>
      <div>
        {parameterDefinitions.map((parameter) => {
          let name = parameter.name;
          let nameId = id(name);
          let type = parameter.type;
          let content;

          if (type.startsWith("select")) {
            content = (
              <div>
                <label htmlFor={"dd" + nameId} className={styles.parameterLabel}>
                  {name}
                </label>
                <Combobox
                  id={"dd" + nameId}
                  placeholder={
                    areItemsLoading[nameId] ? "Loading…" : (items[nameId]?.length ?? 0) == 0 ? "No items" : "Select"
                  }
                  className={styles.parameterControl}
                  size="small"
                  disabled={(items[nameId]?.length ?? 0) == 0}
                  autoComplete="off"
                  onOpenChange={(_, data) => {
                    setComboboxQuery("");
                    setComboboxOpen(nameId);
                    setIsComboboxOpen(data.open);
                  }}
                  onChange={(ev) => setComboboxQuery(ev.target.value.toLowerCase())}
                  value={
                    (isComboboxOpen && comboboxOpen == nameId && comboboxQuery
                      ? comboboxQuery
                      : items[nameId]?.find((p) => p.id === selectedItem[nameId])?.name) ?? ""
                  }
                  selectedOptions={getSelectedOptions(items[nameId], selectedItem[nameId])}
                  onOptionSelect={(_, data) => setSelectedItem(nameId, data.optionValue || "")}
                >
                  {items[nameId]
                    ?.filter((item) => comboboxQuery == "" || item.name.toLowerCase().includes(comboboxQuery))
                    ?.map((item) => (
                      <Option key={item.id} value={item.id}>
                        {item.name}
                      </Option>
                    ))}
                </Combobox>
              </div>
            );
          } else if (type.startsWith("multiselect")) {
            content = (
              <div>
                <label htmlFor={"dd" + nameId} className={styles.parameterLabel}>
                  {name}
                </label>
                <Combobox
                  id={"dd" + nameId}
                  placeholder={
                    areItemsLoading[nameId] ? "Loading…" : (items[nameId]?.length ?? 0) == 0 ? "No items" : "Select"
                  }
                  className={styles.parameterControl}
                  size="small"
                  disabled={(items[nameId]?.length ?? 0) == 0}
                  multiselect={true}
                  autoComplete="off"
                  onOpenChange={(_, data) => {
                    setComboboxQuery("");
                    setComboboxOpen(nameId);
                    setIsComboboxOpen(data.open);
                  }}
                  onChange={(ev) => setComboboxQuery(ev.target.value.toLowerCase())}
                  value={
                    (isComboboxOpen && comboboxOpen == nameId && comboboxQuery
                      ? comboboxQuery
                      : items[nameId]
                          ?.filter((p) => (", " + selectedItem[nameId] + ",").indexOf(", " + p.id + ",") >= 0)
                          ?.map((p) => p.name)
                          ?.join(", ") || comboboxQuery) ?? ""
                  }
                  selectedOptions={getSelectedOptions(items[nameId], selectedItem[nameId])}
                  onOptionSelect={(_, data) => handleOptionSelect(nameId, data)}
                >
                  {!comboboxQuery ? (
                    <Option key="all" value="all" disabled={items[nameId]?.length === 0}>
                      {(parameter.default?.length ?? 0) > 0 ? parameter.default : "Select All"}
                    </Option>
                  ) : null}
                  {items[nameId]
                    ?.filter((item) => comboboxQuery == "" || item.name.toLowerCase().includes(comboboxQuery))
                    ?.map((item) => (
                      <Option key={item.id} value={item.id}>
                        {item.name}
                      </Option>
                    ))}
                </Combobox>
              </div>
            );
          } else if (type == "text" || type == "number") {
            content = (
              <div>
                <label htmlFor={"tb" + nameId} className={styles.parameterLabel}>
                  {name}
                </label>
                <Input
                  id={"tb" + nameId}
                  placeholder={"Enter " + type}
                  className={styles.parameterControl}
                  type={type}
                  value={selectedItem[nameId] || ""}
                  onChange={(_, data) => setSelectedItem(nameId, data.value)}
                  size="small"
                />
              </div>
            );
          } else if (type == "date") {
            content = (
              <div>
                <label htmlFor={"dp" + nameId} className={styles.parameterLabel}>
                  {name}
                </label>
                <DatePicker
                  id={"dp" + nameId}
                  placeholder="Select"
                  className={styles.parameterControl}
                  size="small"
                  value={selectedItem[nameId] ? new Date(selectedItem[nameId]) : null}
                  allowTextInput={true}
                  onSelectDate={(date?: Date) => setSelectedItem(nameId, utc(date)?.toISOString() || "")}
                />
              </div>
            );
          } else {
            content = <div className={styles.parameterWarning}>Unknown parameter type for {name}.</div>;
          }

          return (
            <div key={parameter.name} className={styles.parameter}>
              {content}
            </div>
          );
        })}
      </div>

      {dataDefinitions[selectedDataDefinitionIndex]?.call.endsWith("&udf=*") &&
      <div style={{marginTop: "10px", marginBottom: "10px"}}>
        <label htmlFor={"ddUdfs"} className={styles.parameterLabel}>
          User Defined
        </label>
        <Combobox
          id={"ddUdfs"}
          placeholder={
            areUdfsLoading ? "Loading…" : (udfs?.length ?? 0) == 0 ? "N/A" : "Select"
          }
          className={styles.parameterControl}
          size="small"
          disabled={(udfs?.length ?? 0) == 0}
          multiselect={true}
          autoComplete="off"
          onOpenChange={(_, data) => {
            setUdfComboboxQuery("");
            setIsUdfComboboxOpen(data.open);
          }}
          onChange={(ev) => setUdfComboboxQuery(ev.target.value.toLowerCase())}
          value={
            (isUdfComboboxOpen && udfComboboxQuery
              ? udfComboboxQuery
              : udfs
                  ?.filter((p) => (", " + selectedUdfs + ",").indexOf(", " + p.id + ",") >= 0)
                  ?.map((p) => p.name)
                  ?.join(", ") || udfComboboxQuery) ?? ""
          }
          selectedOptions={getSelectedOptions(udfs, selectedUdfs)}
          onOptionSelect={(_, data) => handleUdfSelect(data)}
        >
          {!udfComboboxQuery ? (
            <Option key="all" value="all" disabled={udfs?.length === 0}>
              {"Select All"}
            </Option>
          ) : null}
          {udfs
            ?.filter((item) => udfComboboxQuery == "" || item.name.toLowerCase().includes(udfComboboxQuery))
            ?.map((item) => (
              <Option key={item.id} value={item.id}>
                {item.name}
              </Option>
            ))}
        </Combobox>
      </div>
      }

      <div className={styles.commandArea}>
        <span style={{ display: "inline-flex", alignItems: "center" }}>
          <Button
            label={"Get " + ((dataDefinitions[selectedDataDefinitionIndex].name?.length ?? 0) > 0 ? dataDefinitions[selectedDataDefinitionIndex].name : "Data")}
            onClick={handleGetDataLocal}
            className={dataDefinitions.length > 1 ? styles.button : styles.wideButton}
            disabled={!canGetData}
          />
          {dataDefinitions.length > 1 && (
            <Menu>
              <MenuTrigger>
                <FluentButton icon={<ChevronDown12Regular />} size="small" className={styles.dropDownButton} style={{ padding: "1px", margin: "0px", marginLeft: "-8px", zIndex: 1 }} />
              </MenuTrigger>
              <MenuPopover>
                <MenuList style={{ marginTop: "8px" }}>
                  {dataDefinitions.map((def, i) => (
                    <MenuItem key={i} 
                      onClick={() => { setSelectedDataDefinitionIndex(i, dataDefinitions); loadDependentDefData(parameterDefinitions, dataDefinitions, selectedItem, i); handleGetData(i); }}
                      disabled={canGetDatas && canGetDatas.length > i && !canGetDatas[i]}>
                      {def.name?.length > 0 ? def.name : "Data"}
                      {i == selectedDataDefinitionIndex ? <Checkmark12Regular style={{ float: "right" }} /> : null}
                    </MenuItem>
                  ))}
                </MenuList>
              </MenuPopover>
            </Menu>
          )}
        </span>
        
        <ConfirmationForm
          isOpen={isGetDataConfirmationOpen}
          onConfirm={() => {
            setIsGetDataConfirmationOpen(false);
            handleGetDataConfirmationClick(selectedDataDefinitionIndex);
          }}
          onCancel={() => setIsGetDataConfirmationOpen(false)}
          dialogTitle="Get data"
          dialogContent="Are you sure you want to overwrite existing data in the workbook?"
          confirmButtonLabel="Overwrite"
          cancelButtonLabel="Cancel"
        />

        <ConfirmationForm
          isOpen={isRefreshQueriesMessageShown}
          onConfirm={() => {
            setIsRefreshQueriesMessageShown(false);
          }}
          dialogTitle="Refresh needed"
          dialogContent="The workbook contains queries that may need to be refreshed to reflect the new data. Please refresh the queries from the Data tab."
          confirmButtonLabel="OK"
        />
      </div>

      <div className={styles.saveCommandArea}>
        <Input
          value={reportName || ""}
          onChange={(_, data) => setReportName(data.value)}
          placeholder="Analyzer name to save as"
          disabled={!gotData}
        />
        <Button label="Save" onClick={handleSave} className={styles.saveButton} disabled={!gotData || !reportName?.trim()?.length } />
        <ConfirmationForm
          isOpen={isSaveConfirmationOpen}
          onConfirm={() => {
            setIsSaveConfirmationOpen(false);
            setOverWrite("true");
            handleSaveConfirmationClick();
          }}
          onCancel={() => setIsSaveConfirmationOpen(false)}
          dialogTitle="Save analyzer"
          dialogContent="Are you sure you want to overwrite the existing analyzer on the server?"
          confirmButtonLabel="Overwrite"
          cancelButtonLabel="Cancel"
        />
        <Dialog open={saveCompleted}>
          <DialogSurface style={{ margin: "16px" }}>
            <DialogBody>
              <DialogTitle>Save</DialogTitle>
              <DialogContent>
                <p>The analyzer has been saved successfully.</p>
              </DialogContent>
              <DialogActions>
                <DialogTrigger disableButtonEnhancement>
                  <Button onClick={() => setSaveCompleted(false)} label="Close" />
                </DialogTrigger>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
        {isLoading && (
          <div className={styles.spinnerContainer}>
            <Spinner size="large" />
            {percent > 0 && (
              <p style={{ color: "white", marginLeft: "20px" }}>
                Loading... {`${percent.toFixed(0)}%`}
              </p>
            )}
          </div>
        )}
        <Dialog open={(error?.length ?? 0) > 0}>
          <DialogSurface style={{ margin: "16px" }}>
            <DialogBody>
              <DialogTitle>Error</DialogTitle>
              <DialogContent>
                <p style={{ color: "red" }}>
                  An error has occurred:
                  <br />
                  {error}
                </p>
              </DialogContent>
              <DialogActions>
                <DialogTrigger disableButtonEnhancement>
                  <Button onClick={() => setError("")} label="Close" />
                </DialogTrigger>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      </div>
    </div>
  );
};

export default ReportParams;
